import { FetchEventAnalyticsGraphParams } from '@models/membership/event/EventAnalyticsGraph'
import { FetchMembershipAnalyticsGraphParams } from '@models/membership/MembershipAnalyticsGraph'
import { FetchUserAnalyticsGraphParams } from '@models/user/UserAnalyticsGraph'
import { FetchVenueAnalyticsGraphParams } from '@models/venue/VenueAnalyticsGraph'
import { SBAPI } from '@supplement-bacon/react-toolkit/utils'
import { localFlags } from '@utils/featureFlags'
import {
  ANALYTICS_GRAPH_URL,
  ANALYTICS_STATISTICS_URL,
  MEMBERSHIP_INCOMING_RENEWALS_URL,
  MEMBERSHIP_TRANSACTIONS_URL,
  USERS_URL,
} from '@utils/urls'
import axios from 'axios'
import qs from 'qs'
import fakeAnalyticsUserEvents from './mock/fakeAnalyticsEvents'
import generateFakeGraphData from './mock/fakeAnalyticsGraph'
import {
  type FakeListQuery,
  fakeIncomingRenewalPayloads,
  fakeMembershipStatistics,
  fakeTransactionPayloads,
} from './mock/fakeMembershipAnalytics'

export type FetchAnalyticsGraphParams =
  | FetchUserAnalyticsGraphParams
  | FetchVenueAnalyticsGraphParams
  | FetchEventAnalyticsGraphParams
  | FetchMembershipAnalyticsGraphParams

export const ANALYTICS_INTERCEPTOR_STORAGE_KEY = 'dev_analytics_interceptor'

export const isAnalyticsInterceptorEnabled = () =>
  localStorage.getItem(ANALYTICS_INTERCEPTOR_STORAGE_KEY) === 'true'

export const setAnalyticsInterceptorEnabled = (enabled: boolean) =>
  localStorage.setItem(ANALYTICS_INTERCEPTOR_STORAGE_KEY, String(enabled))

// Setup interceptor for non-production environments (dev + staging)
if (localFlags.analyticsInterceptor) {
  axios.interceptors.request.use(
    (config) => {
      if (!isAnalyticsInterceptorEnabled()) return config

      // Check if this is an analytics graph request
      if (config.url?.includes(ANALYTICS_GRAPH_URL)) {
        // Extract query string from URL
        const urlParts = config.url.split('?')
        if (urlParts.length > 1) {
          const queryString = urlParts[1]
          const params = qs.parse(
            queryString
          ) as unknown as FetchAnalyticsGraphParams

          const fakeData = generateFakeGraphData(params.entity, params.kind)

          console.log('🎭 [DEV] Intercepted analytics graph request:', {
            kind: params.kind,
            entity: params.entity,
          })

          // Create a custom adapter that returns fake data without making the request
          config.adapter = () => {
            return Promise.resolve({
              data: {
                status: 200,
                data: fakeData,
                message: 'Fake data generated for development',
              },
              status: 200,
              statusText: 'OK',
              headers: {},
              config,
            })
          }
        }
      }
      if (config.url?.includes(USERS_URL) && config.url.includes('/events')) {
        // Extract query string from URL

        const fakeData = fakeAnalyticsUserEvents

        console.log('🎭 [DEV] Intercepted analytics events request:', {
          fakeData,
        })

        // Create a custom adapter that returns fake data without making the request
        config.adapter = () => {
          return Promise.resolve({
            data: {
              status: 200,
              data: fakeData,
              message: 'Fake data generated for development',
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
          })
        }
      }

      // `analytics/statistics` (every membership kind) and the two paginated
      // membership analytics lists. Without these, the KPI cards and the two
      // tables were the only surfaces left calling the real API while the
      // charts were faked - each rendering its own error line.
      if (config.url?.includes(ANALYTICS_STATISTICS_URL)) {
        const params = qs.parse(config.url.split('?')[1] ?? '') as {
          entity?: string
          kind?: string
          endDate?: string
        }

        if (params.entity === 'membership') {
          const fakeData = fakeMembershipStatistics(params.kind ?? '', {
            endDate: params.endDate,
          })

          console.log('🎭 [DEV] Intercepted analytics statistics request:', {
            kind: params.kind,
          })

          config.adapter = () =>
            Promise.resolve({
              data: {
                status: 200,
                data: fakeData,
                message: 'Fake data generated for development',
              },
              status: 200,
              statusText: 'OK',
              headers: {},
              config,
            })
        }
      }

      // The fakes honour the date range and page size of the request, so a
      // "next 7 days, 10 rows" preview does not get the drill-down's list.
      const listQuery = qs.parse(config.url?.split('?')[1] ?? '') as {
        filters?: {
          startDate?: string[]
          endDate?: string[]
          outcome?: string[]
        }
        pagination?: { pageSize?: string }
      }
      const fakeListQuery: FakeListQuery = {
        startDate: listQuery.filters?.startDate?.[0],
        endDate: listQuery.filters?.endDate?.[0],
        outcome: listQuery.filters?.outcome,
        pageSize: listQuery.pagination?.pageSize
          ? Number(listQuery.pagination.pageSize)
          : undefined,
      }

      const paginatedFake =
        config.url?.includes(MEMBERSHIP_TRANSACTIONS_URL) === true
          ? fakeTransactionPayloads(fakeListQuery)
          : config.url?.includes(MEMBERSHIP_INCOMING_RENEWALS_URL) === true
            ? fakeIncomingRenewalPayloads(fakeListQuery)
            : undefined

      if (paginatedFake) {
        console.log('🎭 [DEV] Intercepted membership analytics list request:', {
          rows: paginatedFake.length,
        })

        config.adapter = () =>
          Promise.resolve({
            data: {
              status: 200,
              data: paginatedFake,
              totalRecords: paginatedFake.length,
              currentPage: 1,
              message: 'Fake data generated for development',
            },
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
          })
      }

      return config
    },
    (error) => Promise.reject(error)
  )
}

const fetchAnalyticsGraph = <T = any>(params: FetchAnalyticsGraphParams) =>
  SBAPI.Fetch<T>(ANALYTICS_GRAPH_URL, params)

export { fetchAnalyticsGraph }
